// frontend/src/components/formularios/FormularioDocumentacion.jsx
// FORMULARIO PARA CREAR/EDITAR DOCUMENTACIÓN
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FormularioBase from './FormularioBase';
import AttachmentUploader from '../common/AttachmentUploader.jsx';
import apiClient from '../../services/apiClient';

const FormularioDocumentacion = ({ documento = null, initialEquipo = null, showTitle = true, noNestedScroll = false, compact = false, fullWidth = true, onSave: parentOnSave, onCancel: parentOnCancel, enableSaveAndNew = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id || documento);

  const [initialData, setInitialData] = useState({});
  const [pendingFiles, setPendingFiles] = useState([]);
  const pendingFilesRef = useRef([]);
  pendingFilesRef.current = pendingFiles;
  const [noVence, setNoVence] = useState(false);

  // Campos del formulario basados en el modelo Documentacion (modelo backend).
  // El adjunto ya no es un campo simple del registro: se maneja aparte (ver
  // AttachmentUploader debajo) porque un documento puede tener varios archivos.
  // fecha_vencimiento queda siempre visible pero se deshabilita cuando no_vence
  // está tildado (subField), así el checkbox no se mueve de lugar en el grid.
  const fields = [
    { name: 'equipo', label: 'Equipo', type: 'select', optionsEndpoint: 'equipos/', required: true, searchable: true, searchRemote: true, preserveOnDuplicate: true },
    {
      name: 'tipo', label: 'Tipo de Documentación', type: 'select', optionsEndpoint: 'tiposdocumentacion/', required: true,
      fkConfig: { apiEndpoint: 'tiposdocumentacion', titulo: 'Tipos de Documentación', descripcion: 'Tipos de documentos del sistema', icon: 'fas fa-file-alt' },
      preserveOnDuplicate: true,
    },
    { name: 'fecha_emision', label: 'Fecha de Emisión', type: 'date' },
    {
      name: 'fecha_vencimiento', label: 'Fecha de Vencimiento', type: 'date', required: !noVence,
      disabled: noVence,
    },
    { name: 'es_publico', label: 'Visible en portal QR (sin login)', type: 'checkbox', help: 'Activar para que este documento sea visible para cualquiera que escanee el QR del equipo (ej. seguro, permiso de transporte).' },
    { name: 'no_vence', label: 'Este documento no vence', type: 'checkbox', help: 'Activar para documentos sin fecha de vencimiento, como el título de propiedad.' },
    { name: 'notas', label: 'Notas', type: 'textarea', fullWidth: true },
  ];

  const validationRules = {
    equipo: { required: true },
    tipo: { required: true },
    ...(noVence ? {} : { fecha_vencimiento: { required: true } })
  };

  const handleFieldChange = (name, value, nextData, applyUpdates) => {
    if (name === 'no_vence') {
      setNoVence(Boolean(value));
      if (value) applyUpdates({ fecha_vencimiento: '' });
    }
  };

  // Si se pasó el prop `documento` (por ejemplo al abrir desde la lista en un modal), usarlo.
  // En caso contrario, si estamos en modo edición por URL, cargar desde la API.
  useEffect(() => {
    if (documento) {
      const d = documento;
      setInitialData({
        id: d.id,
        equipo: d.equipo?.id || d.equipo || '',
        tipo: d.tipo?.id || d.tipo || '',
        fecha_emision: d.fecha_emision || '',
        fecha_vencimiento: d.fecha_vencimiento || '',
        no_vence: d.no_vence || false,
        notas: d.notas || '',
        es_publico: d.es_publico || false
      });
      setNoVence(Boolean(d.no_vence));
      return;
    }

    // Si se pasó equipo preseleccionado (ej: desde el perfil del equipo), pre-cargar sin modo edición
    if (!isEditing && initialEquipo) {
      setInitialData({ equipo: String(initialEquipo) });
      return;
    }

    if (!isEditing) return;

    const load = async () => {
      try {
        const resp = await apiClient.get(`documentacion/${id}/`);
        const d = resp.data || {};
        setInitialData({
          id: d.id,
          equipo: d.equipo?.id || d.equipo || '',
          tipo: d.tipo?.id || d.tipo || '',
          fecha_emision: d.fecha_emision || '',
          fecha_vencimiento: d.fecha_vencimiento || '',
          no_vence: d.no_vence || false,
          notas: d.notas || '',
          es_publico: d.es_publico || false
        });
        setNoVence(Boolean(d.no_vence));
      } catch (error) {
        console.error('Error cargando documentación:', error);
      }
    };

    load();
  }, [documento, id, isEditing, initialEquipo]);

  // Al crear el documento, FormularioBase dispara este evento con la respuesta
  // del servidor (incluyendo el id recién creado). Se aprovecha para subir los
  // archivos que el usuario haya seleccionado antes de guardar.
  useEffect(() => {
    const handleRecordSaved = async (e) => {
      const detail = e.detail || {};
      if (detail.endpoint !== 'documentacion/') return;
      const nuevoId = detail.data?.id;
      const files = pendingFilesRef.current;
      if (!nuevoId || files.length === 0) return;
      try {
        const fd = new FormData();
        files.forEach((f) => fd.append('archivos', f));
        await apiClient.post(`documentacion/${nuevoId}/adjuntos/`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } catch (error) {
        console.error('Error subiendo adjuntos de documentación:', error);
      }
    };

    window.addEventListener('gestion-taller:record-saved', handleRecordSaved);
    return () => window.removeEventListener('gestion-taller:record-saved', handleRecordSaved);
  }, []);

  // Manejar guardado exitoso
  const handleSave = (savedData, opts = {}) => {
    if (opts.keepOpen) {
      // "Guardar y nuevo/duplicar": el formulario se queda abierto. Los adjuntos
      // ya se subieron para el registro guardado (ver handleRecordSaved) — nunca
      // deben arrastrarse al próximo documento.
      setPendingFiles([]);
      return;
    }
    console.log('Documentación guardada:', savedData);
    // Volver a la lista de documentación
    navigate('/documentacion');
  };

  // Manejar cancelación
  const handleCancel = () => {
    navigate('/documentacion');
  };

  return (
    <>
      <FormularioBase
        title={isEditing ? 'Editar Documentación' : 'Nueva Documentación'}
        icon="bi bi-file-earmark"
        fields={fields}
        initialData={initialData}
        apiEndpoint="documentacion/"
        isEditing={isEditing}
        validationRules={validationRules}
        onFieldChange={handleFieldChange}
        onSave={parentOnSave || handleSave}
        onCancel={parentOnCancel || handleCancel}
        showTitle={showTitle}
        noNestedScroll={noNestedScroll}
        compact={compact}
        fullWidth={fullWidth}
        enableSaveAndNew={enableSaveAndNew}
      />
      {!isEditing && (
        <AttachmentUploader
          label="Adjuntar archivos"
          helperText="Podés seleccionar varios archivos (póliza, comprobante, fotos, etc.). Se suben al guardar el documento."
          pendingFiles={pendingFiles}
          onFilesSelected={(files) => setPendingFiles((prev) => [...prev, ...files])}
          onRemovePending={(idx) => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
        />
      )}
    </>
  );
};

export default FormularioDocumentacion;
